import { useEffect, useMemo, useRef } from 'react';

import { mergeContextualData } from '../contextualData';
import type { EventTrackingAction } from '../declarations/EventTracking';
import type {
  TrackingOptions,
  UseObservabilityTrackJourneyEvent,
} from '../declarations/Observability';
import type { UserEvent } from '../declarations/UserEvent';
import { globalObservability } from '../global';
import type { SegmentCompatibleData } from '../internal/analytics';
import { getDeployEnv } from '../internal/deployEnv';
import { useContextualData } from './ContextualDataContext';

export type UseObservabilityFunctions<AppCategory = undefined, AppActions = undefined> = {
  trackEvent: (
    action: EventTrackingAction<AppActions>,
    label?: string,
    data?: SegmentCompatibleData<{ [k: string]: unknown }>,
    options?: TrackingOptions<AppCategory>,
  ) => void;
  trackUserEvent: (event: UserEvent) => void;
  trackJourneyEvent: UseObservabilityTrackJourneyEvent;
  trackError: (
    label: string,
    error?: Error,
    data?: SegmentCompatibleData<{ [k: string]: unknown }>,
    options?: TrackingOptions<AppCategory>,
  ) => void;
};

/** Gives you access to Observability methods, with ContextualData from the context automatically added. */
export function useObservability<
  AppCategory extends string | undefined = undefined,
  AppActions extends string | undefined = undefined,
>(): UseObservabilityFunctions<AppCategory, AppActions> {
  const env = getDeployEnv();

  const contextualData = useContextualData();

  // Store contextualData in a ref so we don't update the track functions when contextualData changes.
  // Previously, any useEffect that depended on a track function would get called again whenever
  // contextualData changed, which is not the behavior people were expecting.
  const contextualDataRef = useRef(contextualData);
  useEffect(() => {
    contextualDataRef.current = contextualData;
  }, [contextualData]);

  return useMemo(
    () => ({
      /**
       * Track events not performed by a user but meaningful internally. For events performed
       * by a user, use `trackUserEvent`.
       *
       * TODO (@core-experience-platform): rename to `trackInternalEvent`
       */
      trackEvent: (
        action,
        label,
        data?: SegmentCompatibleData<{ [k: string]: unknown }>,
        options?: TrackingOptions<AppCategory>,
      ) => {
        if (!globalObservability.isInitialized()) {
          if (env === 'production') {
            return;
          }
          throw new Error('Call initObservability before trackEvent');
        }
        globalObservability.trackEvent(
          action,
          label,
          mergeContextualData(contextualDataRef.current, { data }),
          options,
        );
      },
      /**
       * @deprecated Use trackJourneyEvent instead
       * @see https://knowledgebase.apps.opendoor.com/doc/product/customer-demand/product-eng-org/operations-runbooks/how-to-create-journey-2-0-events.md
       */
      trackUserEvent: (event: UserEvent) => {
        if (!globalObservability.isInitialized()) {
          if (env === 'production') {
            return;
          }
          throw new Error('Call initObservability before trackUserEvent');
        }
        globalObservability.trackUserEvent(event, contextualDataRef.current);
      },
      trackJourneyEvent: (categoryName, eventName, eventData, options) => {
        if (!globalObservability.isInitialized()) {
          if (env === 'production') {
            return;
          }
          throw new Error('Call initObservability before trackJourneyEvent');
        }
        globalObservability.trackJourneyEvent(
          categoryName,
          eventName,
          eventData,
          contextualDataRef.current,
          options,
        );
      },
      trackError: (
        label,
        error,
        data?: SegmentCompatibleData<{ [k: string]: unknown }>,
        options?: TrackingOptions<AppCategory>,
      ) => {
        if (!globalObservability.isInitialized()) {
          if (env === 'production') {
            return;
          }
          throw new Error('Call initObservability before trackError');
        }
        globalObservability.trackError(
          label,
          error,
          mergeContextualData(contextualDataRef.current, { data }),
          options,
        );
      },
    }),
    [env],
  );
}
